import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate, useParams, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "@/hooks/useTheme";
import { PartnerVendorProvider } from "@/contexts/PartnerVendorContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { useUtmPersistence } from "@/hooks/useUtmPersistence";
import { captureCreatorRef } from "@/lib/creatorRef";
import { trackPageView } from "@/lib/analytics";
import { COA_GUIDE_HOWTO_SCHEMA } from "@/lib/seo/coa-guide-schema";
import { GUT_GUIDE_HOWTO_SCHEMA } from "@/lib/seo/gut-guide-schema";
import SectionLoader from "@/components/SectionLoader";
import Index from "./pages/Index";
// 2026-05-01 Phase 14b: HardRedirectHead must NOT be lazy-loaded — lovablehtml's
// prerender captures the page BEFORE Suspense resolves, so a lazy import means
// the <meta http-equiv="refresh"> tag is missing from the captured HTML. That's
// the entire reason /coa-reader, /vendor/amino-asylum, and /nootropic/
// benzodiazepines mis-prerender as either self-loops or destination-content-
// under-source-URL Soft 404s. Sync import = meta tag in initial paint = prerender
// detects redirect target deterministically.
import HardRedirectHead from "./components/HardRedirectHead";
import { RETIRED_VENDOR_REDIRECTS } from "@/lib/retiredVendorRedirects";
import DisclaimerGate from "./components/DisclaimerGate";
import { isNoStoreRoute } from "@/lib/storeBarRoutes";
// Site-wide motion rules (no entrance animations, 200ms state changes, reduced motion) and the one fixed bottom
// slot. Both are plain context providers; framer-motion is already in the first-paint graph through the Navbar.
import { MotionPolicy } from "@/components/ds/MotionPolicy";
import { BottomSlotProvider } from "@/components/ds/BottomSlot";

// Lazy-load non-critical UI components
const StickyBottomCTA = lazy(() => import("./components/StickyBottomCTA"));
const FloatingAIChat = lazy(() => import("./components/FloatingAIChat"));
// RecentlyViewed is no longer mounted (see the note where it used to render).
const ExitIntentModal = lazy(() => import("./components/ExitIntentModal"));
const IonRetaLaunchPopup = lazy(() => import("./components/IonRetaLaunchPopup"));
const BHGLabsLaunchPopup = lazy(() => import("./components/BHGLabsLaunchPopup"));

const NotFound = lazy(() => import("./pages/NotFound"));

const CompoundPage = lazy(() => import("./pages/CompoundPage"));
const CompoundVendorPage = lazy(() => import("./pages/CompoundVendorPage"));
const LabResultPage = lazy(() => import("./pages/LabResultPage"));
const CompoundAliasRedirect = lazy(() => import("./pages/CompoundAliasRedirect"));

const VendorDirectory = lazy(() => import("./pages/VendorDirectory"));
const VendorPage = lazy(() => import("./pages/VendorPage"));
const PartnerVendorPage = lazy(() => import("./pages/PartnerVendorPage"));
const SmartVendorPage = lazy(() => import("./pages/SmartVendorPage"));
const VendorArchivePage = lazy(() => import("./pages/VendorArchivePage"));
const ToolsPage = lazy(() => import("./pages/ToolsPage"));
const ReconstitutionCalc = lazy(() => import("./pages/ReconstitutionCalc"));
const CompoundReconstitutionPage = lazy(() => import("./pages/CompoundReconstitutionPage"));
const EmbedReconstitution = lazy(() => import("./pages/EmbedReconstitution"));
const EmbedCalculatorsPromo = lazy(() => import("./pages/EmbedCalculatorsPromo"));
const EmbedTrustBadge = lazy(() => import("./pages/EmbedTrustBadge"));
const GetVettedPage = lazy(() => import("./pages/GetVettedPage"));
const EmbedDosage = lazy(() => import("./pages/EmbedDosage"));
const CoaVerificationGuide = lazy(() => import("./pages/CoaVerificationGuide"));
const CompoundDosageCalculatorPage = lazy(() => import("./pages/CompoundDosageCalculatorPage"));
const IntranasalCalc = lazy(() => import("./pages/IntranasalCalc"));
const HalfLifeVisualizer = lazy(() => import("./pages/HalfLifeVisualizer"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const VendorComparisonPage = lazy(() => import("./pages/VendorComparisonPage"));
const BhgLabsPriceComparisonPage = lazy(() => import("./pages/BhgLabsPriceComparisonPage"));
const CoaReader = lazy(() => import("./pages/CoaReader"));
const DealsPage = lazy(() => import("./pages/DealsPage"));
const PeptidePricesPage = lazy(() => import("./pages/PeptidePricesPage"));
const CouponsPage = lazy(() => import("./pages/CouponsPage"));
// Phase R (2026-04-28): per-compound coupon programmatic-SEO pages.
// Targets "<compound> coupon code" + "cheapest <compound>" transactional queries.
const CompoundCouponPage = lazy(() => import("./pages/CompoundCouponPage"));
// Phase S (2026-04-28): per-vendor coupon programmatic-SEO pages.
// Targets "<vendor> coupon code" + "<vendor> promo" transactional queries.
const VendorCouponPage = lazy(() => import("./pages/VendorCouponPage"));
// Phase T (2026-04-28): per-compound buyer-intent programmatic-SEO pages.
// Targets "where to buy <compound>", "<compound> for sale", "<compound> price".
const BuyCompoundPage = lazy(() => import("./pages/BuyCompoundPage"));
const BestVendorsPage = lazy(() => import("./pages/BestVendorsPage"));
// Phase U (2026-04-28): "best peptides/nootropics for <goal>" pillar pages.
// Targets the highest-volume head keywords in the research-compound SERP space.
const BestForGoalPage = lazy(() => import("./pages/BestForGoalPage"));
// Phase V (2026-04-28): /protocol/<compound> programmatic protocol pages.
// Targets "<compound> protocol" queries (5K-30K monthly searches per compound).
const CompoundProtocolPage = lazy(() => import("./pages/CompoundProtocolPage"));
// Phase W (2026-04-28): /alternatives-to/<compound> switch-intent pages.
// Targets "alternatives to X" / "what to take instead of X" buyer queries.
const AlternativesToCompoundPage = lazy(() => import("./pages/AlternativesToCompoundPage"));
// Phase AA (2026-04-28): /side-effects/<compound> safety/research pages.
// Targets "<compound> side effects" queries (12K-50K monthly per compound).
const CompoundSideEffectsPage = lazy(() => import("./pages/CompoundSideEffectsPage"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminCitations = lazy(() => import("./pages/AdminCitations"));
const CoachingPage = lazy(() => import("./pages/CoachingPage"));
const CoachesDirectory = lazy(() => import("./pages/CoachesDirectory"));
const CoachFactoryNutritionWellness = lazy(() => import("./pages/CoachFactoryNutritionWellness"));
const CoachDashboard = lazy(() => import("./pages/CoachDashboard"));
const CoachProfile = lazy(() => import("./pages/CoachProfile"));
const CoachProtocol = lazy(() => import("./pages/CoachProtocol"));
// Phase N (2026-04-27): /creator/<slug> influencer profile pages.
// Mirrors peptiprices.com's /chloe, /peter, /biobabe, /trenci, /raven pattern.
// Each creator gets a public profile + unique coupon code + featured compounds.
const CreatorPage = lazy(() => import("./pages/CreatorPage"));
// Phase O (2026-04-27): /creators directory listing all active creators.
// Closes the breadcrumb loop on /creator/:slug → /creators.
const CreatorsDirectory = lazy(() => import("./pages/CreatorsDirectory"));
// Phase R (2026-05-06): creator self-service affiliate-code management +
// invite-only signup flow. Lets creators bring their own GoAffPro codes
// and earn directly from BHG-shared content.
const CreatorAffiliateCodes = lazy(() => import("./pages/CreatorAffiliateCodes"));
const CreatorSignup = lazy(() => import("./pages/CreatorSignup"));
const GoalPage = lazy(() => import("./pages/GoalPage"));
const StackBuilder = lazy(() => import("./pages/StackBuilder"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const MetaLanding = lazy(() => import("./pages/MetaLanding"));
const AdLandingPage = lazy(() => import("./pages/AdLandingPage"));
const AdCompareLanding = lazy(() => import("./pages/AdCompareLanding"));
const VendorTrustScorecard = lazy(() => import("./pages/VendorTrustScorecard"));
const VendorDashboard = lazy(() => import("./pages/VendorDashboard"));
const PreviewDesignMockups = lazy(() => import("./pages/PreviewDesignMockups"));
const PreviewDesignMockupsR2 = lazy(() => import("./pages/PreviewDesignMockupsR2"));
const VendorsMethodologyPage = lazy(() => import("./pages/VendorsMethodologyPage"));
// 2026-10-06: the two evidence pages built for AI search, answered from the record (lib/vendorEvidence.ts).
const IsItLegitPage = lazy(() => import("./pages/IsItLegitPage"));
const BhgLabsLegitPage = lazy(() => import("./pages/BhgLabsLegitPage"));
const BhgLabsProductPage = lazy(() => import("./pages/BhgLabsProductPage"));
const BhgLabsProductsIndexPage = lazy(() => import("./pages/BhgLabsProductPage").then(m => ({ default: m.BhgLabsProductsIndexPage })));
const BestPeptideVendorsPage = lazy(() => import("./pages/BestPeptideVendorsPage"));
const AccountSettings = lazy(() => import("./pages/AccountSettings"));
const GuidesPage = lazy(() => import("./pages/GuidesPage"));
const DosingCheatSheetPage = lazy(() => import("./pages/DosingCheatSheetPage"));
const PeptideCheatSheetDownloadPage = lazy(() => import("./pages/PeptideCheatSheetDownloadPage"));
const RetatrutidePhase3CardPage = lazy(() => import("./pages/RetatrutidePhase3CardPage"));
const RetatrutideFaqPage = lazy(() => import("./pages/RetatrutideFaqPage"));
const SemaTirzGuidePage = lazy(() => import("./pages/SemaTirzGuidePage"));
const VendorQuestionPage = lazy(() => import("./pages/VendorQuestionPage"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
const BloodworkAnalyzer = lazy(() => import("./pages/BloodworkAnalyzer"));
const QuizPage = lazy(() => import("./pages/QuizPage"));
const BeginnerGuide = lazy(() => import("./pages/BeginnerGuide"));
const AffiliateDisclosurePage = lazy(() => import("./pages/AffiliateDisclosurePage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const BrainHealthPage = lazy(() => import("./pages/BrainHealthPage"));
const EditorDashboard = lazy(() => import("./pages/EditorDashboard"));
const WikiPage = lazy(() => import("./pages/WikiPage"));
const CompoundHubPage = lazy(() => import("./pages/CompoundHubPage"));
const BlogIndex = lazy(() => import("./pages/BlogIndex"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const SelankBlogPost = lazy(() => import("./pages/SelankBlogPost"));
const NasalSleepStackBlogPost = lazy(() => import("./pages/NasalSleepStackBlogPost"));
const UltimateShredStackBlogPost = lazy(() => import("./pages/UltimateShredStackBlogPost"));
const ReconstituteBpc157BlogPost = lazy(() => import("./pages/ReconstituteBpc157BlogPost"));
const Bpc157VsTb500BlogPost = lazy(() => import("./pages/Bpc157VsTb500BlogPost"));
const AquasomeNanotechOralPeptides = lazy(() => import("./pages/AquasomeNanotechOralPeptides"));
// Internal-only design preview — not in sitemap, not linked from nav.
// V2-V8 + HeroButtons pruned 2026-05-22 (stale iteration). V9 = active.
const PreviewLandingV9 = lazy(() => import("./pages/PreviewLandingV9"));
const PreviewLandingFull = lazy(() => import("./pages/PreviewLandingFull"));
const PreviewLandingOptions = lazy(() => import("./pages/PreviewLandingOptions"));
// The living style guide for components/ds (docs/design/components.md). noindex, not linked from the
// navigation, not in the sitemap and no prerender stub.
const DesignSystemPage = lazy(() => import("./pages/DesignSystemPage"));
const WolverineStackBlogPost = lazy(() => import("./pages/WolverineStackBlogPost"));
const PeptideStorageGuideBlogPost = lazy(() => import("./pages/PeptideStorageGuideBlogPost"));
const BestPeptidesRecoveryPage = lazy(() => import("./pages/BestPeptidesRecoveryPage"));
const BestNootropicsFocusPage = lazy(() => import("./pages/BestNootropicsFocusPage"));
const BestPeptidesWeightLossPage = lazy(() => import("./pages/BestPeptidesWeightLossPage"));
const BestPeptideNasalSprays2026 = lazy(() => import("./pages/BestPeptideNasalSprays2026"));
const GoRedirect = lazy(() => import("./pages/GoRedirect"));
const DosageGuidePage = lazy(() => import("./pages/DosageGuidePage"));
const WomensGuidePage = lazy(() => import("./pages/WomensGuidePage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const ProtocolTracker = lazy(() => import("./pages/ProtocolTracker"));
const EditorialStandardsPage = lazy(() => import("./pages/EditorialStandardsPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const GlossaryPage = lazy(() => import("./pages/GlossaryPage"));
const SiteDirectory = lazy(() => import("./pages/SiteDirectory"));
const TestingDatabase = lazy(() => import("./pages/TestingDatabase"));
const WelcomePeptides = lazy(() => import("./pages/WelcomePeptides"));
const WelcomeNootropics = lazy(() => import("./pages/WelcomeNootropics"));
const WelcomeBeginner = lazy(() => import("./pages/WelcomeBeginner"));
const NootropicsHub = lazy(() => import("./pages/NootropicsHub"));
const NootropicStackBuilder = lazy(() => import("./pages/NootropicStackBuilder"));
const NootropicStackPage = lazy(() => import("./pages/NootropicStackPage"));
const StackHub = lazy(() => import("./pages/StackHub"));
const InteractionChecker = lazy(() => import("./pages/InteractionChecker"));
const NootropicCompoundPage = lazy(() => import("./pages/NootropicCompoundPage"));
const NootropicIndex = lazy(() => import("./pages/NootropicIndex"));
const CognitiveTracker = lazy(() => import("./pages/CognitiveTracker"));
const NootropicsQuiz = lazy(() => import("./pages/NootropicsQuiz"));
const CyclingGenerator = lazy(() => import("./pages/CyclingGenerator"));
const CostCalculator = lazy(() => import("./pages/CostCalculator"));
const CommunityProtocols = lazy(() => import("./pages/CommunityProtocols"));
const NootropicsCompare = lazy(() => import("./pages/NootropicsCompare"));
const DopamineOptimization = lazy(() => import("./pages/brain-health/DopamineOptimization"));
const BdnfNeuroplasticity = lazy(() => import("./pages/brain-health/BdnfNeuroplasticity"));
const SleepArchitecture = lazy(() => import("./pages/brain-health/SleepArchitecture"));
const NeurotransmitterGuide = lazy(() => import("./pages/brain-health/NeurotransmitterGuide"));
const GutBrainConnection = lazy(() => import("./pages/brain-health/GutBrainConnection"));
const BeginnerRoadmap = lazy(() => import("./pages/brain-health/BeginnerRoadmap"));

// Redirect component for old /nootropics/compounds/:slug URLs
const NootropicCompoundRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/nootropic/${slug}`} replace />;
};

/**
 * HardRedirect — alternative to react-router's <Navigate> for alias paths whose
 * prerendered HTML lovablehtml mis-detects.
 *
 * Background (2026-04-30): /coa-reader had a 301 self-loop in the prerender
 * pipeline despite using <Navigate to="/tools/coa" replace />. Same Navigate
 * pattern works for /bloodwork → /tools/bloodwork, but for /coa-reader, /vendor/
 * amino-asylum, and /nootropic/benzodiazepines, lovablehtml's prerender either
 * captured the alias URL as both source AND destination (loop) or rendered the
 * destination's content under the alias URL (Soft 404 + Canonical Mismatch).
 *
 * This component triple-protects:
 *   1. window.location.replace() runs synchronously on render → real browsers
 *      navigate immediately (no SPA hop).
 *   2. <meta http-equiv="refresh"> is emitted into <head> via Helmet — gives
 *      prerender a deterministic redirect signal it can statically detect.
 *   3. canonical points to the destination + noIndex → even if prerender
 *      captures content here, search engines consolidate signal to the right URL.
 */
const HardRedirect = ({ to }: { to: string }) => {
  // 2026-05-01 Phase 14d: drop the JS-driven navigation entirely. Rely
  // SOLELY on the <meta http-equiv="refresh" content="0;url=…"> tag emitted
  // by HardRedirectHead.
  //
  // Why: lovablehtml's prerender pipeline is a headless browser that
  // EXECUTES JavaScript, including useEffect. Any window.location.replace()
  // call (whether in render OR useEffect) navigates the headless browser
  // away from the alias URL before the prerender captures HTML, so the
  // captured page becomes the destination — not the alias — and lovablehtml
  // either caches destination content under the source URL (Soft 404 +
  // Canonical Mismatch) or stores a malformed redirect entry (self-loop).
  //
  // The browser-native meta refresh tag with content="0;url=…" works for
  // both the headless capture AND real users:
  //   - Headless: lovablehtml's static HTML scanner sees the tag and emits
  //     a real 301 to the target URL. The headless browser doesn't need to
  //     navigate — the static signal is enough.
  //   - Real users: their browser honors the meta refresh on receipt;
  //     navigation happens within ~50-200ms (imperceptible). For the small
  //     fraction of bots/clients that ignore meta refresh, the canonical
  //     <link rel="canonical"> + noindex robots tags still consolidate
  //     SEO equity to the destination.
  //
  // Tradeoff: ~50-200ms slower redirect for users vs. instant JS replace.
  // Acceptable cost — this is THE fix for the long-standing /coa-reader
  // self-loop and /vendor/amino-asylum + /nootropic/benzodiazepines Soft 404s.
  return <HardRedirectHead to={to} />;
};

/**
 * Mount UTM persistence + fire page_view on every client-side navigation.
 * Must live inside <BrowserRouter> to access router context.
 */
const RouteListeners = () => {
  useUtmPersistence();
  const location = useLocation();
  useEffect(() => {
    // Phase R (2026-05-06): capture `?ref=<creator_slug>` from any URL the
    // visitor lands on. Sets a 60-day cookie that the /go edge function
    // reads to credit creators on outbound affiliate clicks. Runs on every
    // navigation so a creator-linked URL works whether the visitor lands
    // on the homepage, a blog post, a tool, etc.
    captureCreatorRef(location.search);
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
};

/**
 * BHGChrome — wraps BHG-branded global UI (StickyBottomCTA, FloatingAIChat,
 * ExitIntentModal, AderaLaunchPopup) and suppresses it on partner vendor pages.
 *
 * Partner vendor pages (`/vendors/:slug`) are private, branded-as-vendor
 * landing surfaces. Showing BHG's "Compare Prices" CTA, BHG's AI chat widget,
 * or BHG's cross-sell exit-intent modal on a vendor page:
 *   (a) breaks the "feels like the vendor's site" illusion
 *   (b) can siphon traffic from the vendor's own storefront
 *   (c) contradicts the zero-BHG-attribution intent of the partner hub
 *
 * Keeping the suppression path-based rather than context-based because these
 * components are mounted outside <PartnerVendorProvider>'s render flow.
 */
const BHGChrome = () => {
  const location = useLocation();
  const isVendorPage = location.pathname.startsWith("/vendors/");
  if (isVendorPage) return null;
  return (
    <>
      <Suspense fallback={null}>
        <StickyBottomCTA />
      </Suspense>
      {/* The mobile store bar (StickyStoreBar: "Shop BHG Labs — 20% off, code BHG20") is
          UNMOUNTED as of 2026-10-20 (per owner). It was the only fixed bottom slot on
          mobile and it sent every visitor to one vendor, while the other tracked vendors
          needed the same exposure. StickyBottomCTA now takes the mobile slot itself: a
          vendor-neutral next step (the quiz, or Compare Prices) that points at every
          vendor rather than one store. The component is parked next to the Adera,
          IonReta and BHGLabs popups — to bring it back, add its lazy import above and a
          Suspense block here, and decide which vendor (or rotation) owns the slot. */}
      {!isNoStoreRoute(location.pathname) && <>
      <Suspense fallback={null}>
        <FloatingAIChat />
      </Suspense>
      <Suspense fallback={null}>
        <ExitIntentModal />
      </Suspense>
      {/* Adera popup paused 2026-05-26 — ION drops launch promo is taking the
          corner-toast slot. Re-enable by un-commenting once ION promo cycles out.
      <Suspense fallback={null}>
        <AderaLaunchPopup />
      </Suspense>
      */}
      {/* RChQ BPC-157 caps corner promo paused 2026-06-03 (per owner) — the
          corner-toast slot is now empty. The IonRetaLaunchPopup file holds the
          RChQ content (rotated in-place from IonPeptide on 2026-06-01); both it
          and AderaLaunchPopup are parked, ready to re-enable by un-commenting
          whichever should take the slot next.
      <Suspense fallback={null}>
        <IonRetaLaunchPopup />
      </Suspense>
      */}
      {/* BHG Labs vendor launch popup PAUSED 2026-07-12 (per owner — "been up a
          while"). Parked like the Adera/IonReta blocks above; corner-toast slot
          now empty. Re-enable by un-commenting the Suspense block below. The lazy
          import at the top of this file is intentionally kept so re-enabling is a
          one-line un-comment.
      <Suspense fallback={null}>
        <BHGLabsLaunchPopup />
      </Suspense>
      */}
      </>}
    </>
  );
};

// 2026-04-29: tanstack/query defaults are staleTime:0 + refetchOnWindowFocus:true,
// which made the homepage fire `useCompounds` 3x and `useListings` 4x on a single
// page load (different lazy-mounted components each treating the data as "stale"
// and re-fetching). Setting a 1min staleTime collapses those into a single fetch
// per page; 5min gcTime keeps the cache warm across in-app navigation.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const Loading = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <SectionLoader />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
    <MotionPolicy>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <PartnerVendorProvider>
          <BottomSlotProvider>
          <RouteListeners />
          {/* Skip-to-content link is rendered in index.html so it works before
              hydration. The <main tabIndex={-1}> target here allows focus to
              move programmatically when the link is activated. */}
          {/* The "Recently viewed" strip used to mount here. It rendered at the top of the document,
              hidden behind the fixed navbar, and appeared late for anyone who had viewed two or more
              compounds: an invisible bar that pushed every page down 36px after load (layout shift
              0.08 on a phone, 0.11 on a desktop). components/RecentlyViewed.tsx still records views
              (trackCompoundView); show them inside a page, never above <main>. */}
          <Suspense fallback={null}>
            <DisclaimerGate />
          </Suspense>
          <main id="main-content" tabIndex={-1} className="focus:outline-none">
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/compare" element={<ComparePage />} />
              {/* Programmatic vendor-vs-vendor SEO pages. Static /compare/vendors */}
              {/* segment must come BEFORE /compare/:pair so React Router matches */}
              {/* the literal vendor namespace before falling to compound pairs. */}
              {/* Old profile, coupon and comparison URLs of vendors we no longer list (Adera, GetMelts and
                  others). The list lives in src/lib/retiredVendorRedirects.ts, which the prerender stub
                  generator also reads. Literal paths outrank the :slug and :pair routes. */}
              {Object.entries(RETIRED_VENDOR_REDIRECTS).map(([from, to]) => (
                <Route key={from} path={from} element={<HardRedirect to={to} />} />
              ))}
              <Route path="/compare/vendors/bhg-labs-prices" element={<BhgLabsPriceComparisonPage />} />
              <Route path="/compare/vendors/:pair" element={<VendorComparisonPage />} />
              {/* Programmatic compound-vs-compound SEO pages. Static /compare wins */}
              {/* over /compare/:pair by router ranking; anything with a slug in */}
              {/* the position falls to ComparisonPage which parses "a-vs-b". */}
              <Route path="/compare/:pair" element={<ComparisonPage />} />

              {/* Ion Peptide GLP alias routes — redirect to canonical compound. */}
              {/* React Router v6 ranks static segments above dynamic :slug, so */}
              {/* these always win over the generic /compound/:slug below. */}
              <Route path="/compound/glp-1s" element={<CompoundAliasRedirect />} />
              <Route path="/compound/glp-2t" element={<CompoundAliasRedirect />} />
              <Route path="/compound/glp-3r" element={<CompoundAliasRedirect />} />
              <Route path="/compound/ion-1s" element={<CompoundAliasRedirect />} />
              <Route path="/compound/ion-2t" element={<CompoundAliasRedirect />} />
              <Route path="/compound/ion-3r" element={<CompoundAliasRedirect />} />
              {/* Common malformed variants (no hyphen, lowercase) */}
              <Route path="/compound/glp1s" element={<CompoundAliasRedirect />} />
              <Route path="/compound/glp2t" element={<CompoundAliasRedirect />} />
              <Route path="/compound/glp3r" element={<CompoundAliasRedirect />} />
              <Route path="/compound/ion1s" element={<CompoundAliasRedirect />} />
              <Route path="/compound/ion2t" element={<CompoundAliasRedirect />} />
              <Route path="/compound/ion3r" element={<CompoundAliasRedirect />} />
              <Route path="/compound/ly3437943" element={<CompoundAliasRedirect />} />
              <Route path="/compound/:slug" element={<CompoundPage />} />
              <Route path="/compound/:slug/:vendorSlug" element={<CompoundVendorPage />} />
              <Route path="/lab-results/:slug" element={<LabResultPage kind="cert" />} />
              <Route path="/labs/:slug" element={<LabResultPage kind="lab" />} />
              <Route path="/vendors" element={<VendorDirectory />} />
              {/* /vendors/methodology — published 0-100 rubric. Must be declared
                  BEFORE /vendors/:slug so React Router matches the literal
                  "methodology" segment against this route rather than treating
                  it as a slug param. */}
              <Route path="/vendors/methodology" element={<VendorsMethodologyPage />} />
              <Route path="/vendors/is-it-legit" element={<IsItLegitPage />} />
              <Route path="/vendors/bhg-labs/is-it-legit" element={<BhgLabsLegitPage />} />
              {/* BHG Labs per-product pages from the 2026-10-20 catalog scrape
                  (src/data/bhgLabsProducts.json). Two segments, so no clash with
                  /vendors/:slug. */}
              <Route path="/vendors/bhg-labs/products" element={<BhgLabsProductsIndexPage />} />
              <Route path="/vendors/bhg-labs/products/:slug" element={<BhgLabsProductPage />} />
              <Route path="/best-peptide-vendors" element={<BestPeptideVendorsPage />} />
              {/* Vendor-facing pitch landing for the BHG Verified Vendor program.
                  Counter-pitch to PepReviewPro ($250-2K/mo). Strategy:
                  docs/bhg-vetted-vendor-program-v2-strategy.md. Must be declared
                  BEFORE /vendors/:slug so React Router matches the literal
                  "get-vetted" segment correctly. */}
              <Route path="/vendors/get-vetted" element={<GetVettedPage />} />
              <Route path="/vendors/trust-methodology" element={<Navigate to="/vendors/methodology" replace />} />
              <Route path="/trust-methodology" element={<Navigate to="/vendors/methodology" replace />} />
              {/* Phase M (2026-04-27): public archive of retired vendors with
                  delisting reasons. Must be declared BEFORE /vendors/:slug so
                  the literal "archive" segment matches first. */}
              <Route path="/vendors/archive" element={<VendorArchivePage />} />
              {/* /vendors/scorecard — canonical URL (2026-05-17 swap). MUST be declared
                  BEFORE /vendors/:slug so the literal "scorecard" segment matches
                  VendorTrustScorecard instead of falling through to the SmartVendorPage
                  slug dispatcher (which 404s on a non-existent "scorecard" vendor on the
                  prerender/SSR surface — the "scorecard button goes nowhere" bug). Fixed 2026-06-29. */}
              <Route path="/vendors/scorecard" element={<VendorTrustScorecard />} />
              {/* /vendors/:slug — Phase 15 SmartVendorPage dispatcher.
                  Partner slugs (Ion Peptide) → rich PartnerVendorPage hub.
                  Public slugs (the other 7 active vendors) → VendorPage profile.
                  Unifies the SEO surface so every active vendor has a /vendors/<slug>
                  canonical page that crawlers can discover. */}
              {/* Retired vendors (Adera, GetMelts and others) redirect through RETIRED_VENDOR_REDIRECTS above. */}
              <Route path="/vendors/:slug" element={<SmartVendorPage />} />
              {/* Partner-vendor legacy redirects: old /vendor/<slug> → new /vendors/<slug> hub */}
              <Route path="/vendor/ion-peptide" element={<Navigate to="/vendors/ion-peptide" replace />} />
              <Route path="/vendor/:slug" element={<VendorPage />} />
              <Route path="/tools" element={<ToolsPage />} />
              <Route path="/tools/reconstitution" element={<ReconstitutionCalc />} />
              <Route path="/tools/reconstitution/:slug" element={<CompoundReconstitutionPage />} />
              <Route path="/embed/reconstitution" element={<EmbedReconstitution />} />
              {/* 2026-07-12 RUO compliance: "dosage" in the embed slug is a
                  peptide-ad compliance smell. The canonical embed is now
                  /embed/reconstitution; redirect the legacy slug to it so any
                  existing vendor iframe keeps working while showing the clean,
                  dosing-free calculator. */}
              <Route path="/embed/dosage" element={<Navigate to="/embed/reconstitution" replace />} />
              {/* BHG Vetted trust-badge embed for vendor sites. Pulls live
                  vendor score from prod DB on page load. PRD:
                  docs/bhg-vetted-vendor-program.md */}
              <Route path="/embed/trust-badge" element={<EmbedTrustBadge />} />
              {/* Vendor-facing landing page that pitches the embeddable
                  calculator + provides copy-paste iframe snippets for the
                  five drop-in presets (default, with-cost, white-label,
                  custom-color, dark-theme). Indexable; targets the kw
                  cluster "free peptide calculator embed" + "white-label
                  peptide calculator". */}
              <Route path="/tools/embed-calculators" element={<EmbedCalculatorsPromo />} />
              {/* 2026-07-07 SEO consolidation: /tools/dosage cannibalized the
                  "peptide calculator" cluster (ranked ~pos 77, 0 clicks) and the
                  "dosage" naming is a peptide-ad compliance smell. HardRedirect →
                  the canonical /tools/reconstitution (meta-refresh + canonical +
                  noindex, which lovablehtml records as a 301). The per-compound
                  GLP-1 dosage pages below are distinct long-tail assets and stay. */}
              <Route path="/tools/dosage" element={<HardRedirect to="/tools/reconstitution" />} />
              {/* Per-compound dosage calculators (GLP-1 family). Targets KD0
                  "<compound> dosage calculator" + "dosage chart" demand. */}
              <Route path="/tools/dosage/:slug" element={<CompoundDosageCalculatorPage />} />
              <Route path="/tools/intranasal" element={<IntranasalCalc />} />
              <Route path="/tools/halflife" element={<HalfLifeVisualizer />} />
              <Route path="/tools/coa" element={<CoaReader />} />
              <Route path="/tools/protocol-tracker" element={<ProtocolTracker />} />
              <Route path="/tools/testing-database" element={<TestingDatabase />} />
              <Route path="/tools/nootropic-stack-builder" element={<NootropicStackBuilder />} />
              {/* /stack — research-bench + nootropic-stack hub, indexing every /stack/:slug
                  child page below. Must be declared BEFORE /stack/:slug so the literal
                  "/stack" path wins over the dynamic slug route. Source of truth for both:
                  src/lib/nootropicStacks.ts. Mirrors the STATIC_META["/stack"] title/description
                  in scripts/generate-prerender-stubs.mjs — keep the two in sync. */}
              <Route path="/stack" element={<StackHub />} />
              {/* Programmatic nootropic stack detail pages — one per canonical pre-built stack. */}
              {/* Source of truth: src/lib/nootropicStacks.ts */}
              <Route path="/stack/:slug" element={<NootropicStackPage />} />
              <Route path="/tools/interaction-checker" element={<InteractionChecker />} />
              <Route path="/tools/cognitive-tracker" element={<CognitiveTracker />} />
              <Route path="/tools/nootropics-quiz" element={<NootropicsQuiz />} />
              <Route path="/tools/cycling-generator" element={<CyclingGenerator />} />
              <Route path="/tools/cost-calculator" element={<CostCalculator />} />
              <Route path="/community/protocols" element={<CommunityProtocols />} />
              <Route path="/nootropic/:slug" element={<NootropicCompoundPage />} />
              <Route path="/nootropics/compounds/:slug" element={<NootropicCompoundRedirect />} />
              <Route path="/nootropics/compounds" element={<NootropicIndex />} />
              <Route path="/nootropics/compare" element={<NootropicsCompare />} />
              <Route path="/quiz/nootropics" element={<Navigate to="/tools/nootropics-quiz" replace />} />
              <Route path="/tools/reconstitution-calculator" element={<Navigate to="/tools/reconstitution" replace />} />
              <Route path="/nootropics/beginner-roadmap" element={<Navigate to="/beginner-guide" replace />} />
              <Route path="/deals" element={<DealsPage />} />
              {/* Cross-compound price-comparison hub — targets the "peptide price" head term. */}
              <Route path="/peptide-prices" element={<PeptidePricesPage />} />
              <Route path="/coupons" element={<CouponsPage />} />
              {/* Phase S (2026-04-28): per-vendor coupon pages MUST be declared
                  BEFORE the /coupons/:slug catch-all so the literal "vendor"
                  segment wins. Otherwise React Router would match
                  /coupons/vendor/<slug> as compound slug "vendor" — a 404. */}
              <Route path="/coupons/vendor/:slug" element={<VendorCouponPage />} />
              {/* Phase R (2026-04-28): per-compound coupon programmatic-SEO pages.
                  Static /coupons literal must come BEFORE this so React Router
                  matches it first (which it does — order above). The ":slug"
                  param is matched against compounds.slug. */}
              <Route path="/coupons/:slug" element={<CompoundCouponPage />} />
              {/* Phase T (2026-04-28): /buy/<compound> — most transactional
                  programmatic-SEO surface. Targets "where to buy X" / "X for
                  sale" / "X price" buyer-stage queries. Uses compound slug
                  param matched against compounds.slug. */}
              <Route path="/buy/:slug" element={<BuyCompoundPage />} />
              <Route path="/best-vendors/:slug" element={<BestVendorsPage />} />
              {/* Phase U (2026-04-28): /best/peptides-for-<goal> +
                  /best/nootropics-for-<goal> pillar pages. Single-segment
                  param "fullSlug" parsed inside the component into category
                  prefix + goal slug. Targets head-volume "best X for Y"
                  queries. */}
              <Route path="/best/:fullSlug" element={<BestForGoalPage />} />
              {/* Phase V (2026-04-28): /protocol/<compound> per-compound
                  programmatic protocol pages. HowTo + FAQ schema. Targets
                  "<compound> protocol" head queries. */}
              <Route path="/protocol/:slug" element={<CompoundProtocolPage />} />
              {/* Phase W (2026-04-28): /alternatives-to/<compound> switch-intent
                  programmatic-SEO pages. Recommends alternatives via shared
                  goalData categories + ranks by current vendor availability. */}
              <Route path="/alternatives-to/:slug" element={<AlternativesToCompoundPage />} />
              {/* Phase AA (2026-04-28): /side-effects/<compound> safety pages.
                  MedicalCondition + FAQ schema. Targets "<compound> side
                  effects" queries with 12K-50K monthly searches per major
                  compound. Pivots to vendor sourcing CTA via the
                  research-evidence-based "vendor quality matters for
                  side-effect frequency" pitch. */}
              <Route path="/side-effects/:slug" element={<CompoundSideEffectsPage />} />
              <Route path="/coaching" element={<CoachingPage />} />
              {/* /coach/<partner-slug> — partner coaching intake pages (hidden, not in
                  sitemap or nav). Each partner gets their own branded landing page.
                  Built 2026-05-27 starting with /coach/factory-nutrition-wellness for
                  FNW (San Diego coach with nationwide service). Doubles as documentation
                  for the partner's payment processor showing the BHG referral pipeline. */}
              <Route path="/coach/factory-nutrition-wellness" element={<CoachFactoryNutritionWellness />} />
              {/* /coach — partner-coach login + dashboard. Auth-gated; coaches see
                  only their own partner's coaching_requests rows (scoped via RLS).
                  Built 2026-05-27 as the FNW-and-future-partner CRM. Admin invites
                  via /admin → Coach Logins tab → invite-partner-coach edge fn. */}
              <Route path="/coach" element={<CoachDashboard />} />
              {/* /coaches (plural) = live directory + per-coach profile + per-protocol pages.
                  /coaching (singular) above remains the marketing/waitlist landing.
                  Static /coaches/methodology-style routes (none yet) MUST be registered
                  ABOVE the /coaches/:slug catch-all if added later. */}
              <Route path="/coaches" element={<CoachesDirectory />} />
              <Route path="/coaches/:slug/protocols/:protocolSlug" element={<CoachProtocol />} />
              <Route path="/coaches/:slug" element={<CoachProfile />} />
              {/* Phase N (2026-04-27): influencer creator program. Each row in
                  the `creators` table renders here at /creator/<slug>. The page
                  gracefully no-ops via React Query null fallback when the
                  migration hasn't been applied yet (PGRST205/42P01 swallowed). */}
              <Route path="/creator/:slug" element={<CreatorPage />} />
              {/* Phase O: public directory of active creators. Companion landing
                  page for /creator/<slug>'s breadcrumb parent. */}
              <Route path="/creators" element={<CreatorsDirectory />} />
              <Route path="/goal/:slug" element={<GoalPage />} />
              <Route path="/stack-builder" element={<StackBuilder />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              {/* /lp = legacy hardcoded Meta LP, kept for existing ad URLs.
                  /lp/compare/:slug = ad-variant single-compound landing page (Phase 10b).
                  Static route MUST come before /lp/:slug catch-all so it wins.
                  /lp/:slug = dynamic LP driven by ad_landing_pages table. */}
              <Route path="/lp" element={<MetaLanding />} />
              <Route path="/lp/compare/:slug" element={<AdCompareLanding />} />
              <Route path="/lp/:slug" element={<AdLandingPage />} />
              {/* /vendors/scorecard is the canonical URL per lovablehtml AI report
                  (2026-05-17 — Smartsheet beats us on 'vendor scorecard' queries
                  because they have a clean canonical URL; we were 302-redirecting,
                  killing the canonical signal). /vendor-trust-scorecard now
                  redirects to the canonical to preserve old inbound links. */}
              <Route path="/vendor-trust-scorecard" element={<Navigate to="/vendors/scorecard" replace />} />
              <Route path="/settings" element={<AccountSettings />} />
              {/* Phase R (2026-05-06): creator self-service. Auth-gated; signed-in
                  users without a creator profile get an empty state pointing them
                  to /signup/creator. */}
              <Route path="/account/affiliate-codes" element={<CreatorAffiliateCodes />} />
              <Route path="/signup/creator" element={<CreatorSignup />} />
              <Route path="/guides" element={<GuidesPage />} />
              <Route path="/guides/dosage/:slug" element={<DosageGuidePage />} />
              <Route path="/guides/dosing-cheat-sheet" element={<DosingCheatSheetPage />} />
              <Route path="/guides/peptide-cheat-sheet-download" element={<PeptideCheatSheetDownloadPage />} />
              <Route path="/guides/retatrutide-phase-3-card" element={<RetatrutidePhase3CardPage />} />
              <Route path="/guides/retatrutide-faq" element={<RetatrutideFaqPage />} />
              <Route path="/guides/semaglutide-vs-tirzepatide" element={<SemaTirzGuidePage />} />
              {/* 2026-10-06: folded into the record pages (one address per question); the guide text lives there now. */}
              <Route path="/guides/are-peptide-vendors-legit" element={<HardRedirect to="/vendors/is-it-legit" />} />
              <Route path="/guides/best-us-peptide-vendors" element={<HardRedirect to="/best-peptide-vendors" />} />
              <Route path="/guides/how-to-check-peptide-lab-report" element={<VendorQuestionPage slug="how-to-check-peptide-lab-report" />} />
              <Route path="/guides/peptide-price-vs-quality" element={<VendorQuestionPage slug="peptide-price-vs-quality" />} />
              <Route path="/guides/womens-guide" element={<WomensGuidePage />} />
              <Route path="/guides/coa-verification" element={<CoaVerificationGuide />} />
              {/* 2026-07-07: KD-0 "how to reconstitute peptides" guide. Backed by a
                  blog_posts row rendered through BlogPost (slugOverride), canonical at
                  the /guides/ path; a rich prerender stub bakes the article for crawlers.
                  Placed before /guides/:slug so it wins over the GuidePage catch-all. */}
              <Route path="/guides/how-to-reconstitute-peptides" element={<BlogPost slugOverride="how-to-reconstitute-peptides" canonicalPath="/guides/how-to-reconstitute-peptides" />} />
              <Route path="/guides/:slug" element={<GuidePage />} />
              <Route path="/tools/bloodwork" element={<BloodworkAnalyzer />} />
              <Route path="/bloodwork" element={<HardRedirect to="/tools/bloodwork" />} />
              <Route path="/tools/quiz" element={<QuizPage />} />
              <Route path="/beginner-guide" element={<BeginnerGuide />} />
              <Route path="/affiliate-disclosure" element={<AffiliateDisclosurePage />} />
              {/* /how-we-make-money retired 2026-06-29 — redirect it and its
                  aliases to /affiliate-disclosure, which carries the formal FTC
                  16 CFR Part 255 statement plus the affiliate-vendor disclosure. */}
              <Route path="/how-we-make-money" element={<Navigate to="/affiliate-disclosure" replace />} />
              <Route path="/how-we-earn" element={<Navigate to="/affiliate-disclosure" replace />} />
              <Route path="/revenue" element={<Navigate to="/affiliate-disclosure" replace />} />
              <Route path="/transparency" element={<Navigate to="/affiliate-disclosure" replace />} />
              <Route path="/editorial-standards" element={<EditorialStandardsPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/glossary" element={<GlossaryPage />} />
              <Route path="/site-directory" element={<SiteDirectory />} />
              {/* Common aliases for the site map — redirect to canonical /site-directory */}
              <Route path="/sitemap" element={<Navigate to="/site-directory" replace />} />
              <Route path="/site-map" element={<Navigate to="/site-directory" replace />} />
              <Route path="/all-pages" element={<Navigate to="/site-directory" replace />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/brain-health" element={<BrainHealthPage />} />
              <Route path="/brain-health/dopamine-optimization" element={<DopamineOptimization />} />
              <Route path="/brain-health/bdnf-neuroplasticity" element={<BdnfNeuroplasticity />} />
              <Route path="/brain-health/sleep-architecture" element={<SleepArchitecture />} />
              <Route path="/brain-health/neurotransmitter-guide" element={<NeurotransmitterGuide />} />
              <Route path="/brain-health/gut-brain-connection" element={<GutBrainConnection />} />
              <Route path="/guides/nootropics-beginner-roadmap" element={<BeginnerRoadmap />} />
              {/* /trust-score → /vendors/scorecard (flattened from a former double
                  redirect; the canonical /vendors/scorecard route now lives up with the
                  other literal /vendors/* routes, before /vendors/:slug). */}
              <Route path="/trust-score" element={<Navigate to="/vendors/scorecard" replace />} />
              {/* /vendors/dashboard — login-gated vendor self-serve surface.
                  Shows trust score, history graph, click attribution, upgrade
                  CTAs for Pro/Growth tiers. Vendor account → vendor linkage
                  is admin-curated via vendor_dashboard_users table. */}
              <Route path="/vendors/dashboard" element={<VendorDashboard />} />
              {/* 2026-05-12: preview-only routes for design exploration. Compares
                  5 hero-background options using BHG's actual hero copy. Not
                  linked from the live site. Dev builds only since 2026-09-13,
                  like the landing previews below: both pages are labelled "NOT
                  for production" and carried no noindex. */}
              {import.meta.env.DEV && <Route path="/preview/design" element={<PreviewDesignMockups />} />}
              {import.meta.env.DEV && <Route path="/preview/design-v2" element={<PreviewDesignMockupsR2 />} />}
              <Route path="/stacks" element={<Navigate to="/stack-builder" replace />} />
              <Route path="/tools/peptide-finder" element={<Navigate to="/tools/quiz" replace />} />
              {/* 2026-05-01 Phase 14g: short-form aliases for natural URLs users
                  type. Each currently returned a Soft 404 (HTTP 200 SPA shell with
                  client-side NotFound page). <Navigate> works for these because
                  the destinations are normal SPA pages — no HardRedirect-style
                  prerender confusion (we learned in Phase 14e that anything
                  emitting a meta refresh or window.location signal during render
                  breaks lovablehtml's prerender capture). */}
              <Route path="/quiz" element={<Navigate to="/tools/quiz" replace />} />
              {/* 2026-05-04 audit: smoke test caught these two as Soft 404s. Both
                  are plausible URLs a user might type or follow from external
                  backlinks. Add Navigate so they land on the canonical pages. */}
              <Route path="/compounds" element={<CompoundHubPage />} />
              <Route path="/nootropics/quiz" element={<Navigate to="/tools/nootropics-quiz" replace />} />
              <Route path="/protocol" element={<Navigate to="/tools/protocol-tracker" replace />} />
              <Route path="/protocol-tracker" element={<Navigate to="/tools/protocol-tracker" replace />} />
              <Route path="/coa" element={<Navigate to="/tools/coa" replace />} />
              <Route path="/reconstitution" element={<Navigate to="/tools/reconstitution" replace />} />
              <Route path="/calculator" element={<Navigate to="/tools/reconstitution" replace />} />
              <Route path="/dosage" element={<Navigate to="/tools/reconstitution" replace />} />
              <Route path="/halflife" element={<Navigate to="/tools/halflife" replace />} />
              <Route path="/intranasal" element={<Navigate to="/tools/intranasal" replace />} />
              {/* 2026-04-30: was <Navigate> — lovablehtml prerender produced a 301
                  self-loop (X-Lovablehtml-Redirect: /coa-reader → /coa-reader).
                  HardRedirect uses window.location.replace + meta refresh so the
                  prerender pipeline detects the redirect target deterministically. */}
              {/* 2026-05-01 Phase 14e: render the destination component directly
                  instead of attempting a redirect. lovablehtml's prerender pipeline
                  evaluates pages in a real headless browser that honors meta refresh
                  AND useEffect navigation — so any redirect attempt causes the
                  prerender to capture the destination's content under the source URL
                  and persist a malformed redirect-cache entry (manifesting as 301
                  self-loop or Soft 404). Rendering CoaReader under /coa-reader gives
                  users the right content, the canonical inside CoaReader points to
                  /tools/coa for SEO consolidation, and lovablehtml gets a clean
                  page entry it can prerender deterministically. */}
              <Route path="/coa-reader" element={<HardRedirect to="/tools/coa" />} />
              <Route path="/goal/cognition" element={<Navigate to="/goal/focus" replace />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/citations" element={<AdminCitations />} />
              <Route path="/editor" element={<EditorDashboard />} />
              <Route path="/wiki" element={<WikiPage />} />
              {/* Legacy /cheatsheet URL → merged into /guides/peptide-cheat-sheet-download
                  (quick-lookup tables now live inline on the download page). */}
              <Route path="/cheatsheet" element={<HardRedirect to="/guides/peptide-cheat-sheet-download" />} />
              <Route path="/welcome/peptides" element={<WelcomePeptides />} />
              <Route path="/welcome/nootropics" element={<WelcomeNootropics />} />
              <Route path="/welcome/beginner" element={<WelcomeBeginner />} />
              <Route path="/nootropics" element={<NootropicsHub />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/blog" element={<BlogIndex />} />
              {/* How to Read a Peptide COA — served at the TOP-LEVEL URL (the
                  Reddit link points here). Reuses the BlogPost renderer against
                  the DB post but with canonical = the top-level path + a HowTo
                  schema for the 60-second steps. The /blog/<slug> variant 301s
                  here so there's a single canonical URL. */}
              <Route path="/how-to-read-a-peptide-coa" element={<BlogPost slugOverride="how-to-read-a-peptide-coa" canonicalPath="/how-to-read-a-peptide-coa" extraSchema={[COA_GUIDE_HOWTO_SCHEMA]} />} />
              <Route path="/blog/how-to-read-a-peptide-coa" element={<Navigate to="/how-to-read-a-peptide-coa" replace />} />
              {/* The reconstitution guide lives at /guides/…; the blog_posts row it
                  reads is also matchable at /blog/… — redirect that to the canonical. */}
              <Route path="/blog/how-to-reconstitute-peptides" element={<Navigate to="/guides/how-to-reconstitute-peptides" replace />} />
              {/* Consolidate the older near-duplicate COA post into the new canonical URL. */}
              <Route path="/blog/how-to-read-peptide-coa" element={<Navigate to="/how-to-read-a-peptide-coa" replace />} />
              {/* Gut Healing Supplements guide — DB post rendered by BlogPost, with a
                  HowTo schema added on top of its auto BlogPosting + FAQPage. Standard
                  /blog/ path, so canonical is native. */}
              <Route path="/blog/gut-healing-supplements-research-guide" element={<BlogPost slugOverride="gut-healing-supplements-research-guide" extraSchema={[GUT_GUIDE_HOWTO_SCHEMA]} />} />
              <Route path="/blog/selank-nasal-spray-protocol" element={<SelankBlogPost />} />
              <Route path="/blog/nasal-sleep-stack-protocol" element={<NasalSleepStackBlogPost />} />
              <Route path="/blog/ultimate-shred-stack-retatrutide-tesamorelin-protocol" element={<UltimateShredStackBlogPost />} />
              <Route path="/blog/how-to-reconstitute-bpc-157" element={<ReconstituteBpc157BlogPost />} />
              <Route path="/blog/bpc-157-vs-tb-500" element={<Bpc157VsTb500BlogPost />} />
              {/* AQASOME oral peptide post — hardcoded ION-themed page per user spec 2026-05-20.
                  Brand spelling is "AQASOME" (no U), so canonical URL uses the matching lowercase form. */}
              <Route path="/blog/aqasome-nanotechnology-oral-peptides" element={<AquasomeNanotechOralPeptides />} />
              {/* Internal homepage design previews (2026-05-22). Dev builds only since 2026-09-10: they show a BHG
                  Labs card with retired codes and product names and no ownership note, so production sends these
                  paths to the not-found route. */}
              {import.meta.env.DEV && <Route path="/preview/landing-v9" element={<PreviewLandingV9 />} />}
              {import.meta.env.DEV && <Route path="/preview/landing-full" element={<PreviewLandingFull />} />}
              {import.meta.env.DEV && <Route path="/preview/landing-options" element={<PreviewLandingOptions />} />}
              {/* The design system's living style guide: every primitive in components/ds in its states. noindex,
                  not linked from the navigation, not in the sitemap, no prerender stub. */}
              <Route path="/design-system" element={<DesignSystemPage />} />
              {/* 301 redirect from the PubMed-spelling slug variant ("aquasome" with U) back to brand-spelling canonical. */}
              <Route path="/blog/aquasome-nanotechnology-oral-peptides" element={<Navigate to="/blog/aqasome-nanotechnology-oral-peptides" replace />} />
              <Route path="/blog/wolverine-stack-bpc-157-tb-500-protocol-guide" element={<WolverineStackBlogPost />} />
              {/* Soft-404 fixes — external links use the shorter slug variants */}
              <Route path="/blog/wolverine-stack-bpc157-tb500-protocol" element={<Navigate to="/blog/wolverine-stack-bpc-157-tb-500-protocol-guide" replace />} />
              <Route path="/blog/wolverine-stack" element={<Navigate to="/blog/wolverine-stack-bpc-157-tb-500-protocol-guide" replace />} />
              <Route path="/blog/bpc157-tb500-stack" element={<Navigate to="/blog/wolverine-stack-bpc-157-tb-500-protocol-guide" replace />} />
              {/* 2026-10-25: never a database post (a soft 404 crawlers saw as "Blog"); the guide is the 2026 one.
                  The prerender stub mirrors this (REDIRECT_ALIASES in scripts/generate-prerender-stubs.mjs). */}
              <Route path="/blog/bpc-157-protocol-guide" element={<Navigate to="/blog/bpc-157-protocol-guide-2026" replace />} />
              {/* Adera retired 2026-06-29 — the old vendor review redirects to the BHG Labs review that replaced it. */}
              <Route path="/blog/adera-state-review-5-states-system" element={<Navigate to="/blog/bhg-labs-review" replace />} />
              {/* Compound/nootropic/vendor soft-404 redirects — GSC flagged these as missing */}
              <Route path="/nootropic/uridine" element={<Navigate to="/compound/uridine-monophosphate" replace />} />
              <Route path="/compound/n-acetyl-l-tyrosine" element={<Navigate to="/compound/l-tyrosine" replace />} />
              {/* Also catch the /nootropic/ variant — LovableHTML flagged this as Soft 404 on 2026-04-21 */}
              <Route path="/nootropic/n-acetyl-l-tyrosine" element={<Navigate to="/compound/l-tyrosine" replace />} />
              {/* 2026-04-30: HardRedirect (was <Navigate>) — lovablehtml prerender
                  rendered home/destination content under the alias URL, causing Soft
                  404 + Canonical Mismatch. window.location.replace + meta refresh
                  forces a deterministic redirect detection. */}
              {/* 2026-05-01 Phase 14e: same logic as /coa-reader — render the
                  destination directly. The canonical link inside each destination
                  component already points to its own URL, so SEO equity flows
                  naturally. */}
              <Route path="/vendor/amino-asylum" element={<HardRedirect to="/vendors" />} />
              <Route path="/nootropic/benzodiazepines" element={<NeurotransmitterGuide />} />
              {/* LovableHTML canonical mismatch fixes — /nootropic/* slugs not in nootropic_compounds table route to /compound/* */}
              <Route path="/nootropic/bromantane" element={<Navigate to="/compound/bromantane" replace />} />
              <Route path="/nootropic/cdp-choline" element={<Navigate to="/compound/cdp-choline" replace />} />
              <Route path="/nootropic/phenibut" element={<HardRedirect to="/nootropics" />} />
              <Route path="/nootropic/sulbutiamine" element={<Navigate to="/compound/sulbutiamine" replace />} />
              {/* Legacy /nootropics/compounds/* structure → /compound/* */}
              <Route path="/nootropics/compounds/selank" element={<Navigate to="/compound/selank" replace />} />
              <Route path="/nootropics/compounds/semax" element={<Navigate to="/compound/semax" replace />} />
              <Route path="/blog/peptide-storage-guide" element={<PeptideStorageGuideBlogPost />} />
              <Route path="/blog/best-peptides-for-recovery" element={<BestPeptidesRecoveryPage />} />
              <Route path="/blog/best-nootropics-for-focus" element={<BestNootropicsFocusPage />} />
              <Route path="/blog/best-peptides-for-weight-loss" element={<BestPeptidesWeightLossPage />} />
              <Route path="/blog/best-peptide-nasal-sprays-2026" element={<BestPeptideNasalSprays2026 />} />
              <Route path="/go/:slug" element={<GoRedirect />} />
              {/* Cloaked clean-path affiliate redirect. No merchant/compound in
                  the URL — reuses the /go machinery against the research-guide2
                  redirects row (-> BHG Labs BHG-3, ref=zmgqyxku). Caddy ignores
                  the _redirects shim, so this React route is the real mechanism. */}
              <Route path="/research-guide2" element={<GoRedirect fixedSlug="research-guide2" />} />
              {/* Retired review: adera-state-review DB row was deleted (soft-404) but
                  still ranks ~pos 4 for "adera state"/"blitzed adera" (~450 impr/mo).
                  We de-partnered from Adera; bhg-labs-review is the current pick.
                  Crawlers get the redirect via public/blog/adera-state-review-.../index.html
                  (directory-index shim, canonical+meta-refresh); this handles in-app nav. */}
              <Route path="/blog/adera-state-review-5-states-system" element={<Navigate to="/blog/bhg-labs-review" replace />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </main>
          <BHGChrome />
          </BottomSlotProvider>
          </PartnerVendorProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </MotionPolicy>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
